// Firestore sync — Firestore is the authoritative source of truth. localStorage
// (lib/storage.js) is only an offline-read cache: every save() still writes there first
// (instant, works offline) but the cloud copy is what's trusted once it answers.
//
// Ported from the original app's FIREBASE / OFFLINE BANNER section (attachFirestoreSync,
// pushToFirestore, the sync status badge).
//
// Race condition this preserves on purpose (see PORT_STATUS.md bug list #2): if this
// device has an unsaved local edit while another device's change arrives via onSnapshot,
// we must NOT blindly overwrite it. `hasLocalEditSinceInit` guards that window. It's
// cleared once our own push actually confirms — but if a push never confirms (tab
// backgrounded mid-request, network dropped, phone locked at the wrong moment), the flag
// could get stuck true forever and silently block every future incoming update. The 20s
// timeout below is the self-recovery: it retries the push, which on success clears the
// flag again. This was hard-won in the original app — ported exactly, not simplified.
//
// That in-memory flag only protects a single continuous session, though — it resets to
// false on every fresh page load. A real data-loss case: add something, the tab gets
// killed/backgrounded (phone locked, iOS suspending the PWA, force-quit) before the push
// confirms, then the app is reopened later. On that fresh boot the flag is false again, so
// attachFirestoreSync() below would happily pull the older Firestore snapshot and stomp
// the newer local edit that never made it up — permanent data loss, not just a stale read.
// `readPendingSync`/`setPendingSync` (lib/storage.js) persist the same signal to
// localStorage so it survives the reload too; attachFirestoreSync() checks it before ever
// trusting a pulled snapshot over what's on the device.
import {
  doc, getDoc, getDocFromServer, setDoc, onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase.js';
import { S, applyState, saveCache } from '../state.js';
import { cicon } from './icons.js';
import { readPendingSync, setPendingSync } from './storage.js';
import { today } from './dates.js';

let currentUser = null;
let unsubscribeSnapshot = null;
let saveDebounceTimer = null;
let onRemoteChange = null; // callback the app registers to re-render after a remote update

export let hasLocalEditSinceInit = false;
let localEditTimeout = null;

export function setCurrentUser(user) {
  currentUser = user;
}

export function setOnRemoteChange(cb) {
  onRemoteChange = cb;
}

function userDocRef(uid) {
  return doc(db, 'users', uid);
}

// ── Daily backup snapshot ──
// Extra safety net, per explicit request after a day's data went missing: once per real
// calendar day, alongside the normal save, also write the full state to a SEPARATE
// top-level collection that nothing else ever overwrites (userBackups/{uid}_{date} — a
// top-level collection, not a subcollection of users/{uid}, to match the same Firestore
// security-rules shape already proven to work for userIcons/{uid}). If the live document
// is ever corrupted or clobbered by a sync race, a same-day-or-recent snapshot still
// exists to recover from — see Settings → Данные → "Восстановить из бэкапа по дате".
// Fire-and-forget: never blocks or delays the real save, and a failure here is silently
// non-fatal (it'll just retry tomorrow). NOTE: like userIcons, this needs its collection
// allow-listed in Firestore Security Rules to actually work — see DEPLOY.md.
const LAST_BACKUP_KEY = 'levelup_lastBackup_v1';
function maybeWriteDailyBackup() {
  if (!currentUser) return;
  let lastDate = null;
  try { lastDate = localStorage.getItem(LAST_BACKUP_KEY); } catch (e) {}
  const d = today();
  if (lastDate === d) return;
  setDoc(doc(db, 'userBackups', currentUser.uid + '_' + d), {
    data: JSON.stringify(S),
    savedAt: serverTimestamp(),
  }).then(() => {
    try { localStorage.setItem(LAST_BACKUP_KEY, d); } catch (e) {}
  }).catch(err => console.error('daily backup failed', err.message));
}

/**
 * Call after any mutation to `S`. Writes to the local cache immediately, then pushes to
 * Firestore (debounced ~200ms unless `immediate` is true — see known-bugs #6: one-shot
 * actions like checkboxes/buttons should pass immediate=true, typed fields should not).
 */
export function save(immediate) {
  saveCache();
  hasLocalEditSinceInit = true;
  setPendingSync(true); // persisted twin of the flag above — survives a reload, see top-of-file note
  clearTimeout(localEditTimeout);
  localEditTimeout = setTimeout(() => {
    if (hasLocalEditSinceInit && currentUser) pushToFirestore(true);
  }, 20000);
  pushToFirestore(immediate);
}

export function pushToFirestore(immediate) {
  if (!currentUser) return;
  clearTimeout(saveDebounceTimer);
  setSyncStatus('syncing');
  const doPush = () => {
    setDoc(userDocRef(currentUser.uid), {
      data: JSON.stringify(S),
      updatedAt: serverTimestamp(),
    }).then(() => {
      setSyncStatus('synced');
      hasLocalEditSinceInit = false;
      setPendingSync(false);
      clearTimeout(localEditTimeout);
      maybeWriteDailyBackup();
    }).catch(err => { console.error(err); setSyncStatus('error'); });
  };
  if (immediate) doPush(); else saveDebounceTimer = setTimeout(doPush, 200);
}

export function attachFirestoreSync() {
  if (!currentUser) return;
  const ref = userDocRef(currentUser.uid);
  setSyncStatus('syncing');

  // If the previous session ended with an edit that never confirmed as synced (tab killed,
  // phone locked, app force-quit mid-push), don't start this session by pulling the cloud
  // baseline over it — that would silently discard whatever the person added. Push the
  // local cache (already loaded into S before this runs) up first instead.
  if (readPendingSync()) {
    hasLocalEditSinceInit = true;
    pushToFirestore(true);
  } else {
    hasLocalEditSinceInit = false; // about to fetch a fresh baseline from the cloud
  }

  getDoc(ref).then(snap => {
    if (hasLocalEditSinceInit) {
      // Either the branch above just set this, or the person already changed something
      // while this fetch was in flight — either way, local is newer, don't clobber it.
      pushToFirestore(true);
    } else if (snap.exists() && snap.data().data) {
      try {
        const remote = JSON.parse(snap.data().data);
        applyState(remote);
        saveCache();
        onRemoteChange && onRemoteChange();
        pushToFirestore(true); // push the migrated/cleaned-up version back
      } catch (e) { console.error('Failed to parse remote data', e); }
    } else {
      pushToFirestore(true); // no remote doc yet — seed it with local data
    }

    if (unsubscribeSnapshot) unsubscribeSnapshot();
    unsubscribeSnapshot = onSnapshot(ref, snap => {
      if (snap.metadata.hasPendingWrites) return; // echo of our own write — ignore
      if (hasLocalEditSinceInit) return; // a local edit is still working its way up — don't undo it
      if (snap.exists() && snap.data().data) {
        try {
          const remote = JSON.parse(snap.data().data);
          applyState(remote);
          saveCache();
          onRemoteChange && onRemoteChange();
        } catch (e) { console.error('Failed to parse remote update', e); }
      }
      setSyncStatus('synced');
    }, err => { console.error(err); setSyncStatus('error'); });

    setSyncStatus('synced');
  }).catch(err => { console.error(err); setSyncStatus('error'); });
}

export function detachFirestoreSync() {
  if (unsubscribeSnapshot) { unsubscribeSnapshot(); unsubscribeSnapshot = null; }
}

// ── Sync status badge ──
const STATUS_MAP = {
  synced: { text: 'Синхронизировано', icon: 'cloud', color: 'var(--accent)', bg: 'rgba(29,155,95,.1)' },
  syncing: { text: 'Синхронизация…', icon: 'sync', spin: true, color: 'var(--text3)', bg: 'rgba(0,0,0,.05)' },
  offline: { text: 'Локально', icon: 'offline', color: 'var(--text3)', bg: 'rgba(0,0,0,.05)' },
  error: { text: 'Ошибка синхронизации', icon: 'warning', color: 'var(--red)', bg: 'rgba(220,38,38,.1)' },
};

function badgeHtml(icon, text, spin) {
  return `${cicon(icon, 13, spin ? 'cicon-spin' : '')} ${text}`;
}

// Home header badge: just a colored cloud dot, no text — green means synced, red means
// anything else (syncing/offline/error), per explicit request. Kept separate from the
// full text+icon badge in Settings, which stays as-is.
function homeDotHtml(synced) {
  return `<span class="status-dot-cloud" style="width:18px;height:18px;color:${synced ? 'var(--accent)' : 'var(--red)'}"></span>`;
}

export function setSyncStatus(s) {
  const m = STATUS_MAP[s] || STATUS_MAP.offline;
  const el = document.getElementById('syncStatusBadge');
  if (el) { el.innerHTML = badgeHtml(m.icon, m.text, m.spin); el.style.color = m.color; }
  // Every page's small header dot (Home has the original tap-to-verify one, Food/Workout/
  // Vitamins have a read-only twin) — visible confirmation that saving is really happening,
  // not just on the one screen you happen to be looking at when it changes.
  document.querySelectorAll('.sync-dot').forEach(dot => { dot.innerHTML = homeDotHtml(s === 'synced'); dot.style.background = 'none'; });
}

// Real check, not just optimistic UI: pushes current state, waits for actual server
// confirmation, then re-reads it back from the server to prove it's really there.
export async function checkSyncNow() {
  const homeEl = document.getElementById('homeSyncBadge');
  const settingsEl = document.getElementById('syncStatusBadge');
  if (!currentUser) {
    if (homeEl) homeEl.innerHTML = homeDotHtml(false);
    return;
  }
  if (homeEl) homeEl.innerHTML = `<span class="status-dot-cloud cicon-spin" style="width:18px;height:18px;color:var(--text3)"></span>`;
  if (settingsEl) { settingsEl.innerHTML = badgeHtml('sync', 'Проверяю…', true); settingsEl.style.color = 'var(--text3)'; }
  try {
    const payload = JSON.stringify(S);
    await setDoc(userDocRef(currentUser.uid), { data: payload, updatedAt: serverTimestamp() });
    const snap = await getDocFromServer(userDocRef(currentUser.uid));
    const ok = snap.exists() && snap.data().data === payload;
    if (homeEl) homeEl.innerHTML = homeDotHtml(ok);
    if (settingsEl) {
      settingsEl.innerHTML = ok ? badgeHtml('check', 'Подтверждено сервером') : badgeHtml('warning', 'Не совпадает — попробуй ещё раз');
      settingsEl.style.color = ok ? 'var(--accent)' : 'var(--red)';
    }
    hasLocalEditSinceInit = false;
    setPendingSync(false);
    clearTimeout(localEditTimeout);
    setTimeout(() => setSyncStatus('synced'), 3000);
  } catch (e) {
    console.error('checkSyncNow failed', e);
    if (homeEl) homeEl.innerHTML = homeDotHtml(false);
    if (settingsEl) { settingsEl.innerHTML = badgeHtml('warning', 'Не удалось проверить — ' + e.message); settingsEl.style.color = 'var(--red)'; }
  }
}

// ── Offline banner — network connectivity, not auth state. Data always saves to the
// local cache regardless; this just tells the person cloud sync is paused. ──
export function updateOfflineBanner() {
  const banner = document.getElementById('offlineBanner');
  if (!banner) return;
  banner.classList.toggle('show', !navigator.onLine);
}

export function initNetworkWatch() {
  updateOfflineBanner();
  window.addEventListener('online', () => {
    updateOfflineBanner();
    if (currentUser) pushToFirestore(true); // nudge a fresh push so nothing typed offline is stranded
  });
  window.addEventListener('offline', updateOfflineBanner);
}
